import { useCallback, useEffect, useState } from "react";
import { Euro, ShoppingBag, TrendingUp, Percent, PackageX, Eye, Users, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiAnalytics, eur, errMsg } from "../adminApi";

const PAY_LABELS = {
  paid: "Pagato", pending: "In attesa", failed: "Fallito", refunded: "Rimborsato", mismatch: "Discrepanza",
};

function Kpi({ label, value, icon: Icon, loading, testid }) {
  return (
    <Card className="border-line bg-surface2" data-testid={testid}>
      <CardContent className="flex items-start justify-between gap-2 p-4">
        <div className="min-w-0">
          <p className="text-xs font-medium text-steel">{label}</p>
          {loading ? <Skeleton className="mt-2 h-7 w-20" /> : (
            <p className="mt-1 truncate font-display text-2xl font-bold tabular-nums text-chalk">{value}</p>
          )}
        </div>
        <Icon className="h-5 w-5 shrink-0 text-tertiary" />
      </CardContent>
    </Card>
  );
}

function Chart({ data }) {
  const maxRev = Math.max(1, ...data.map((d) => d.revenue));
  const maxVis = Math.max(1, ...data.map((d) => d.visits));
  return (
    <div className="flex h-56 items-end gap-[3px]" data-testid="analytics-chart">
      {data.map((d) => (
        <div key={d.date} className="group relative flex flex-1 flex-col items-center justify-end gap-[2px]" title={`${d.date} · ${eur(d.revenue)} · ${d.visits} visite`}>
          <div className="w-full rounded-t-[2px] bg-lime/80 transition-colors group-hover:bg-lime" style={{ height: `${(d.revenue / maxRev) * 75}%`, minHeight: d.revenue > 0 ? 2 : 0 }} />
          <div className="w-full rounded-t-[2px] bg-steel/40 transition-colors group-hover:bg-steel/70" style={{ height: `${(d.visits / maxVis) * 22}%`, minHeight: d.visits > 0 ? 2 : 0 }} />
        </div>
      ))}
    </div>
  );
}

function BarList({ title, rows, max, valueFmt = (v) => v }) {
  const m = Math.max(1, max ?? Math.max(1, ...rows.map((r) => r.value)));
  return (
    <Card className="border-line bg-surface2">
      <CardHeader className="pb-2"><CardTitle className="font-display text-base">{title}</CardTitle></CardHeader>
      <CardContent className="flex flex-col gap-2.5">
        {rows.length === 0 ? (
          <p className="text-sm text-steel">Nessun dato nel periodo.</p>
        ) : rows.map((r, i) => (
          <div key={i} className="flex flex-col gap-1" data-testid="analytics-bar-row">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 truncate text-chalk">{r.label}</span>
              <span className="shrink-0 tabular-nums text-steel">{valueFmt(r.value)}</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface3">
              <div className="h-full rounded-full bg-lime/70" style={{ width: `${(r.value / m) * 100}%` }} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function Analytics() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    setData(null);
    apiAnalytics(parseInt(days, 10)).then(setData).catch((e) => setError(errMsg(e)));
  }, [days]);
  useEffect(load, [load]);

  const k = data?.kpis;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Statistiche</h1>
          <p className="mt-1 text-sm text-steel">Andamento vendite e traffico del negozio.</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={days} onValueChange={setDays}>
            <TabsList data-testid="analytics-range-tabs">
              <TabsTrigger value="7">7 giorni</TabsTrigger>
              <TabsTrigger value="30">30 giorni</TabsTrigger>
              <TabsTrigger value="90">90 giorni</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="secondary" size="sm" onClick={load} data-testid="analytics-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive/40 bg-surface2"><CardContent className="flex items-center justify-between p-4">
          <p className="text-sm text-chalk">{error}</p>
          <Button variant="secondary" size="sm" onClick={load}>Riprova</Button>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-7">
        <Kpi label="Ricavato" value={eur(k?.revenue)} icon={Euro} loading={!data} testid="kpi-revenue" />
        <Kpi label="Ordini" value={k?.orders ?? 0} icon={ShoppingBag} loading={!data} testid="kpi-orders" />
        <Kpi label="Valore medio" value={eur(k?.aov)} icon={TrendingUp} loading={!data} testid="kpi-aov" />
        <Kpi label="Conversione" value={`${k?.conversion_rate ?? 0}%`} icon={Percent} loading={!data} testid="kpi-conversion" />
        <Kpi label="Abbandonati" value={k?.abandoned ?? 0} icon={PackageX} loading={!data} testid="kpi-abandoned" />
        <Kpi label="Visite" value={k?.visits ?? 0} icon={Eye} loading={!data} testid="kpi-visits" />
        <Kpi label="Visitatori" value={k?.unique_visitors ?? 0} icon={Users} loading={!data} testid="kpi-visitors" />
      </div>

      <Card className="border-line bg-surface2">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-4 font-display text-base">
            Andamento
            <span className="flex items-center gap-1.5 text-xs font-normal text-steel"><span className="h-2 w-2 rounded-sm bg-lime/80" /> Ricavato</span>
            <span className="flex items-center gap-1.5 text-xs font-normal text-steel"><span className="h-2 w-2 rounded-sm bg-steel/50" /> Visite</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data ? <Skeleton className="h-56 w-full" /> : <Chart data={data.timeseries} />}
        </CardContent>
      </Card>

      {data && (
        <div className="grid gap-4 lg:grid-cols-2">
          <BarList title="Prodotti più venduti" rows={(data.top_products || []).map((p) => ({ label: `${p.name}`, value: p.revenue }))} valueFmt={(v) => eur(v)} />
          <BarList title="Sorgenti di traffico" rows={(data.top_sources || []).map((s) => ({ label: s.source, value: s.visits }))} />
          <BarList title="Pagine più viste" rows={(data.top_pages || []).map((p) => ({ label: p.path, value: p.visits }))} />
          <Card className="border-line bg-surface2">
            <CardHeader className="pb-2"><CardTitle className="font-display text-base">Clienti &amp; pagamenti</CardTitle></CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-line bg-surface3 p-3 text-center">
                  <p className="font-display text-2xl font-bold tabular-nums text-lime">{data.customers?.new ?? 0}</p>
                  <p className="text-xs text-steel">Nuovi clienti</p>
                </div>
                <div className="rounded-lg border border-line bg-surface3 p-3 text-center">
                  <p className="font-display text-2xl font-bold tabular-nums text-chalk">{data.customers?.returning ?? 0}</p>
                  <p className="text-xs text-steel">Ricorrenti</p>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {Object.entries(data.status_breakdown || {}).map(([st, n]) => (
                  <div key={st} className="flex items-center justify-between text-sm">
                    <span className="text-steel">{PAY_LABELS[st] || st}</span>
                    <span className="tabular-nums text-chalk">{n}</span>
                  </div>
                ))}
                {Object.keys(data.status_breakdown || {}).length === 0 && <p className="text-sm text-steel">Nessun ordine nel periodo.</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
